import { useEffect, useRef, useState } from "react";

function roundForDisplay(n, decimals) {
  if (decimals > 0) {
    const factor = 10 ** decimals;
    return Math.round(n * factor) / factor;
  }
  return Math.round(n);
}

/**
 * Count-up animation when the element enters the viewport.
 * Optional loop: reaches `to`, pauses, resets to `from`, repeats.
 */
export default function CountUp({
  to,
  from = 1,
  decimals = 0,
  duration = 2400,
  suffix = "",
  prefix = "",
  className = "",
  formatter = (n) => n.toLocaleString("ko-KR"),
  testId,
  loop = false,
  loopPause = 800,
}) {
  const [value, setValue] = useState(() => roundForDisplay(from, decimals));
  const ref = useRef(null);
  const started = useRef(false);
  const rafRef = useRef(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    let cancelled = false;

    const clearTimers = () => {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      if (timeoutRef.current != null) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };

    const runCycle = () => {
      if (cancelled) return;
      const startVal = roundForDisplay(from, decimals);
      setValue(startVal);
      const startTs = performance.now();
      const range = to - from;
      let lastDisplayed = startVal;

      const tick = (now) => {
        if (cancelled) return;
        const elapsed = now - startTs;
        const t = Math.min(elapsed / duration, 1);
        const eased = 1 - Math.pow(1 - t, 3);
        const next = t < 1 ? roundForDisplay(from + range * eased, decimals) : roundForDisplay(to, decimals);

        if (next !== lastDisplayed) {
          lastDisplayed = next;
          setValue(next);
        }

        if (t < 1) {
          rafRef.current = requestAnimationFrame(tick);
        } else if (loop) {
          timeoutRef.current = setTimeout(() => {
            runCycle();
          }, loopPause);
        }
      };

      rafRef.current = requestAnimationFrame(tick);
    };

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !started.current) {
            started.current = true;
            runCycle();
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -5% 0px" }
    );

    io.observe(el);
    return () => {
      cancelled = true;
      clearTimers();
      io.disconnect();
      started.current = false;
    };
  }, [to, from, decimals, duration, loop, loopPause]);

  return (
    <span ref={ref} data-testid={testId} className={className}>
      {prefix}
      {formatter(value)}
      {suffix}
    </span>
  );
}
